import React from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import useTouristicSiteTranslation from '../../../hooks/touristic_sites/useTouristicSiteTranslation';
import {formatAddress, mapsUrl, formatDuration} from '../../../utils/touristic_sites/_v3/siteData';
import {Card, Eyebrow, SectionHead, Ic} from '../../../_ui/_v3/primitives';

const CopyStyled = styled.div`
  p {
    color: ${props => props.theme.colors.inkSoft};
    max-width: 68ch;
    margin: 0 0 .9rem;
    white-space: pre-wrap;
  }
`;

const QuoteStyled = styled.blockquote`
  color: ${props => props.theme.colors.inkSoft};
  font-style: italic;
  border-left: 3px solid ${props => props.theme.colors.brand};
  padding-left: .9rem;
  margin: 1.3rem 0;

  .who {
    display: block;
    font-style: normal;
    font-weight: 700;
    color: ${props => props.theme.colors.ink};
    font-size: .82rem;
    margin-top: .4rem;
  }
`;

const TipsListStyled = styled.ul`
  margin: 1.3rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: .6rem;

  li {
    padding-left: 1.1rem;
    position: relative;
    color: ${props => props.theme.colors.inkSoft};
    font-size: .92rem;
  }
  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: .55em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.theme.colors.brand};
  }
  b { color: ${props => props.theme.colors.ink}; }
`;

// Bandeau horizontal d'infos pratiques (pleine largeur sous la description)
const FactBandStyled = styled(Card)`
  margin-top: 1.8rem;
  padding: 1.1rem 1.4rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1.1rem 2.4rem;
`;

const FactItemStyled = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: .75rem;
  row-gap: .05rem;
  min-width: 170px;

  .ic-wrap {
    grid-row: 1 / 3;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: ${props => props.theme.colors.paperSunk};
    color: ${props => props.theme.colors.brandText};
    font-size: 1.15rem;
  }
  .k {
    align-self: end;
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: ${props => props.theme.colors.inkFaint};
  }
  .v {
    align-self: start;
    font-size: .92rem;
    font-weight: 600;
    color: ${props => props.theme.colors.ink};
  }
  a.v {
    color: ${props => props.theme.colors.brandText};
    text-decoration: none;
  }
  a.v:hover { text-decoration: underline; }
`;

const FactSourceStyled = styled.div`
  margin-top: .9rem;
  font-size: .74rem;
  color: ${props => props.theme.colors.inkFaint};
`;

const PinIcon = () => (
  <Ic viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.4" />
  </Ic>
);
const GlobeIcon = () => (
  <Ic viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3c2.6 2.8 2.6 15.2 0 18M12 3c-2.6 2.8-2.6 15.2 0 18" />
  </Ic>
);
const PhoneIcon = () => (
  <Ic viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 3h3l2 5-2.5 1.5a11 11 0 0 0 5 5L17 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 4 5a2 2 0 0 1 2-2z" />
  </Ic>
);
const ClockIcon = () => (
  <Ic viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 2" />
  </Ic>
);

const FactItem = ({icon, label, children}) => (
  <FactItemStyled>
    <span className="ic-wrap">{icon}</span>
    <span className="k">{label}</span>
    {children}
  </FactItemStyled>
);

const localized = (obj, lang) => obj && (obj[`text_${lang}`] || obj.text_fr);

const websiteLabel = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_) {
    return url;
  }
};

const InfoSection = ({site}) => {
  const {t, i18n} = useTranslation(['touristic-site']);
  const {description} = useTouristicSiteTranslation(site);
  const specificities = localized(site.specificities, i18n.language);
  const tips = site.touristic_site_tips || [];
  const ambassadorContent = site.ambassador_review && site.ambassador_review.content;
  const firstComment = site.feedbacks && site.feedbacks.comments && site.feedbacks.comments[0];
  const quote = ambassadorContent || (firstComment && firstComment.content);
  const quoteAuthor = ambassadorContent
    ? t('reviews.name')
    : (firstComment && ((firstComment.user && firstComment.user.user_profile && firstComment.user.user_profile.firstname) || firstComment.username));
  const address = formatAddress(site);
  const itinerary = mapsUrl(site);
  const duration = formatDuration(site.duration_minutes);
  const hasPracticalFacts = !!(address || site.website_url || site.phone_number || duration);
  const hasCredits = !!(site.credits && site.datatourisme_last_update);

  return (
    <>
      <SectionHead>
        <Eyebrow>{t('v3.eyebrow.infos')}</Eyebrow>
        <h2>{t('v3.section.infos')}</h2>
      </SectionHead>
      <CopyStyled>
        {description && <p>{description}</p>}
        {specificities && <p>{specificities}</p>}
        {quote && (
          <QuoteStyled>
            « {quote} »
            {quoteAuthor && <span className="who">{quoteAuthor}</span>}
          </QuoteStyled>
        )}
        {tips.length > 0 && (
          <TipsListStyled>
            {tips.map((tip, index) => (
              <li key={index}>
                <b>{localized(tip.title, i18n.language)}</b>
                {localized(tip.description, i18n.language) && <> — {localized(tip.description, i18n.language)}</>}
              </li>
            ))}
          </TipsListStyled>
        )}
      </CopyStyled>
      {hasPracticalFacts && (
        <FactBandStyled>
          {address && (
            <FactItem icon={<PinIcon />} label={t('v3.fact.address')}>
              {itinerary
                ? <a className="v" href={itinerary} target="_blank" rel="noopener noreferrer">{address}</a>
                : <span className="v">{address}</span>}
            </FactItem>
          )}
          {site.website_url && (
            <FactItem icon={<GlobeIcon />} label={t('v3.fact.website')}>
              <a className="v" href={site.website_url} target="_blank" rel="noopener noreferrer">{websiteLabel(site.website_url)}</a>
            </FactItem>
          )}
          {site.phone_number && (
            <FactItem icon={<PhoneIcon />} label={t('v3.fact.phone')}>
              <a className="v" href={`tel:${site.phone_number}`}>{site.phone_number}</a>
            </FactItem>
          )}
          {duration && (
            <FactItem icon={<ClockIcon />} label={t('v3.fact.duration')}>
              <span className="v">{duration}</span>
            </FactItem>
          )}
        </FactBandStyled>
      )}
      {hasCredits && (
        <FactSourceStyled>© {site.credits} / DATAtourisme - {site.datatourisme_last_update}</FactSourceStyled>
      )}
    </>
  );
};

export default InfoSection;
